import type { IRecordListParams, IRecordTypeDefinition, IStatusSet, PresetKey } from '@platform/core/types';
import { openClosedOf } from '@platform/core/lib/recordCounts';
import type { IKpiSource } from './IKpiSource';
import type { IOverviewKpi } from './IOverviewKpi';
import type { KpiKey } from './KpiKey';

/** Чотирнадцять діб у мс — поріг показника «Без змін 14 днів». */
const STALE_MS = 14 * 86_400_000;

/** Межа «без змін 14 днів» для часу `now` (мс): мить рівно 14 діб тому, ISO 8601 (прототип: Date.now() − updatedAt > 14 діб). */
export function staleSinceOf(now: number): string {
  return new Date(now - STALE_MS).toISOString();
}

/** Фільтр показника «Без змін 14 днів» (filter-dsl): відкриті записи, змінені раніше за мить `since` (точно, як у прототипі, а не з півночі). */
export function staleFilterOf(since: string): string {
  return `isClosed:eq:false;updatedAt:lt:${since}`;
}

const presets: Partial<Record<KpiKey, PresetKey>> = { open: 'open', overdue: 'overdue', due7: 'due7', unassigned: 'unassigned' };
/** Показники, що потребують уваги: ненульове значення має тон попередження (прострочені — небезпеки). */
const attentionKeys: readonly KpiKey[] = ['overdue', 'due7', 'unassigned', 'stale'];

/**
 * Показники типу запису: строкові — лише з полем `due`, «Не призначено» — лише з полем `assignee` (як у прототипі; приховані поля лише для
 * даних — напр. assignee/due бюджетних статей — прототип у полях модуля не має).
 */
export function kpiKeysOf(rt: IRecordTypeDefinition): KpiKey[] {
  const has = (key: string) => rt.fields.some((f) => f.key === key && !f.hidden);
  return ['total', 'open', ...(has('due') ? (['overdue', 'due7'] as const) : []), ...(has('assignee') ? (['unassigned'] as const) : []), 'stale'];
}

/** Параметри запиту статистики (`by=status`) для показника: пресети списку або фільтр (`since` — межа «без змін 14 днів», staleSinceOf). */
export function kpiParamsOf(key: KpiKey, since: string): IRecordListParams {
  if (key === 'stale') return { filter: staleFilterOf(since) };
  const preset = presets[key];
  return preset && key !== 'open' ? { preset } : {};
}

/** Показник зі статистики: total/open — із загальної статистики за статусами, решта — total відфільтрованої. */
export function kpiOf(key: KpiKey, source: IKpiSource | undefined, statuses: IStatusSet): IOverviewKpi {
  const unavailable = Boolean(source?.error);
  const stats = source?.data;
  const value = unavailable || !stats ? undefined : key === 'open' ? openClosedOf(stats, statuses).open : stats.total;
  const tone = value && key === 'overdue' ? 'bad' : value && attentionKeys.includes(key) ? 'warn' : 'neutral';
  return { key, value, tone, loading: !stats && !unavailable, unavailable, preset: presets[key] };
}
